var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import React, { Component, createRef, Fragment } from 'react';
import { Breakpoint, Placement } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType, Container } from '@philips-internal/oswald-ui-core';
import { EditorContext } from '@philips-internal/oswald-ui-core/lib/component/context/index.js';
import { arrayOf, bool, element, eventListener, expression, number, object, oneOfType } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ClickEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, getBreakpoint, getPercentage, isEmptyArrayOrObject, isEnumValid, isEqualShallow, isFunction, propagateProps, shallowCompare } from '@philips-internal/oswald-ui-utils';
import { BulletTrail } from '../bullet-trail/bullet-trail.js';
import { SuppressHeadingMargin } from '../heading/suppress-heading-margin/suppress-heading-margin.js';
import { NotFound } from '../not-found/not-found.js';
import { ProgressBar } from '../progress-bar/progress-bar.js';
import { CarouselAPI } from './carousel-api.js';
import { CarouselButton } from './components/carousel-button.js';
import './carousel.css';
const CarouselContainer = {
    acceptedTypes: [
        ComponentType.BEHAVIOR,
        ComponentType.DATA_DISPLAY,
    ],
    name: '',
};
const ARROW_LEFT = '/static/assets/components/arrow-left-xsm-light.svg';
const ARROW_RIGHT = '/static/assets/components/arrow-right-xsm-light.svg';
const CAROUSEL_SPACINGS = 13;
const MOBILE_ITEMS_PER_PAGE = 1;
let Carousel = class Carousel extends Component {
    constructor() {
        super(...arguments);
        this.carouselItemsContainerRef = createRef();
        this.state = {
            autoplayCurrentTime: 0,
            page: 0,
            pageItems: [],
        };
        this.initalTouch = 0;
        this.lastTouch = 0;
        this.touchMoveX = 0;
        this.startAutoplay = () => {
            const { autoplaySpeed } = this.props;
            this.resetAutoplayCurrentTime();
            if (this.countUpInterval || autoplaySpeed <= 0) {
                return;
            }
            this.countUpInterval = setInterval(() => {
                const { autoplayCurrentTime } = this.state;
                if (autoplayCurrentTime === autoplaySpeed) {
                    return this.onNavRight();
                }
                this.setState((prevState) => ({ autoplayCurrentTime: prevState.autoplayCurrentTime + 1 }));
            }, 1000);
        };
        this.stopAutoplay = () => {
            if (this.countUpInterval) {
                clearInterval(this.countUpInterval);
                this.countUpInterval = undefined;
            }
        };
        this.onClick = (item) => {
            const { onClick } = this.props;
            if (isFunction(onClick)) {
                onClick(new ClickEvent({ value: item }));
            }
        };
        this.onDoubleClick = (item) => {
            const { onDoubleClick } = this.props;
            if (isFunction(onDoubleClick)) {
                onDoubleClick(new ClickEvent({ value: item }));
            }
        };
        this.onNavRight = () => {
            const { infinite } = this.props;
            const { page, pageItems } = this.state;
            this.stopAutoplay();
            if ((pageItems.length - 1) > page) {
                return this.setState((prevState) => ({ page: prevState.page + 1 }), this.afterNavigate);
            }
            if (infinite) {
                this.setState({ page: 0 }, this.afterNavigate);
            }
        };
        this.onNavLeft = () => {
            const { infinite } = this.props;
            const { page, pageItems } = this.state;
            this.stopAutoplay();
            if (page > 0) {
                return this.setState((prevState) => ({ page: prevState.page - 1 }), this.afterNavigate);
            }
            if (infinite) {
                this.setState({ page: pageItems.length - 1 }, this.afterNavigate);
            }
        };
        this.onTouchEnd = () => {
            if (this.lastTouch > this.initalTouch) {
                this.onNavLeft();
            }
            if (this.lastTouch < this.initalTouch) {
                this.onNavRight();
            }
            this.initalTouch = 0;
            this.lastTouch = 0;
        };
        this.onTouchMove = (event) => {
            const current = this.carouselItemsContainerRef?.current;
            if (!current) {
                return;
            }
            const isInitialTouchGreaterThanLast = this.initalTouch > this.lastTouch;
            const moveDifference = isInitialTouchGreaterThanLast
                ? this.initalTouch - this.lastTouch
                : this.lastTouch - this.initalTouch;
            const newX = isInitialTouchGreaterThanLast
                ? this.touchMoveX + moveDifference
                : this.touchMoveX - moveDifference;
            current.scrollTo(newX, 0);
            this.lastTouch = event.nativeEvent.touches[0].clientX;
        };
        this.onTouchStart = (event) => {
            this.initalTouch = event.nativeEvent.touches[0].clientX;
            this.lastTouch = event.nativeEvent.touches[0].clientX;
        };
    }
    componentDidMount() {
        this.startAutoplay();
    }
    componentDidUpdate(prevProps, prevState) {
        const { autoplaySpeed, data } = this.props;
        const { autoplaySpeed: prevAutoplaySpeed, data: oldData } = prevProps;
        const { page } = this.state;
        const { page: prevPage } = prevState;
        if (data === oldData || (isEqualShallow(data, oldData))) {
            return;
        }
        this.updatePageItems();
        if (autoplaySpeed !== prevAutoplaySpeed || page < prevPage) {
            this.stopAutoplay();
            this.startAutoplay();
        }
    }
    shouldComponentUpdate(nextProps, nextState) {
        const stateChanged = shallowCompare(this.state, nextState, [
            'autoplayCurrentTime',
            'page',
            'pageItems',
        ]);
        if (stateChanged) {
            return true;
        }
        return shallowCompare(this.props, nextProps, [
            'children',
            'data',
            'itemPerPage',
        ]);
    }
    componentWillUnmount() {
        this.stopAutoplay();
    }
    isMobile() {
        const breakpoint = getBreakpoint();
        return breakpoint === Breakpoint.XSM || breakpoint === Breakpoint.SM;
    }
    resetAutoplayCurrentTime(callback) {
        this.setState({ autoplayCurrentTime: 0 }, callback);
    }
    updatePageItems() {
        const { data, itemsPerPage } = this.props;
        if (!Array.isArray(data) || isEmptyArrayOrObject(data)) {
            return;
        }
        const currentItemsPerPage = this.isMobile() ? MOBILE_ITEMS_PER_PAGE : itemsPerPage;
        const pageItems = data.reduce((accumulator, item) => {
            if (accumulator.length === 0) {
                accumulator.push([item]);
                return accumulator;
            }
            const lastPage = accumulator[accumulator.length - 1];
            if (lastPage.length >= currentItemsPerPage) {
                return [...accumulator, [item]];
            }
            lastPage.push(item);
            return accumulator;
        }, []);
        this.setState({ pageItems });
    }
    afterNavigate() {
        if (!this.isMobile()) {
            return this.startAutoplay();
        }
        const { page, pageItems } = this.state;
        const current = this.carouselItemsContainerRef?.current;
        const allPagesItems = pageItems.flat();
        if (!current) {
            return;
        }
        const carouselItemWidth = (current.scrollWidth / allPagesItems.length);
        const x = (carouselItemWidth * page) - CAROUSEL_SPACINGS || 0;
        this.touchMoveX = x;
        current.scrollTo(x, 0);
        this.startAutoplay();
    }
    renderContent() {
        const { data } = this.props;
        const { pageItems } = this.state;
        if (this.context?.isDesigning()) {
            return this.renderContainer();
        }
        if (isEmptyArrayOrObject(data) || isEmptyArrayOrObject(pageItems)) {
            return null;
        }
        return this.isMobile()
            ? this.renderMobile()
            : this.renderDesktop();
    }
    renderProgressBar(customPercentage) {
        const { autoplaySpeed } = this.props;
        const { autoplayCurrentTime } = this.state;
        const percentage = customPercentage ?? getPercentage(autoplayCurrentTime, autoplaySpeed);
        if (autoplaySpeed <= 0) {
            return null;
        }
        return (React.createElement(ProgressBar, { percentage: percentage }));
    }
    renderDesktop() {
        const { page, pageItems } = this.state;
        return (React.createElement(Fragment, null,
            React.createElement("div", { className: "c-carousel__items" }, pageItems[page].map((item, index) => {
                return this.renderCarouselItem(item, index);
            })),
            this.renderProgressBar()));
    }
    renderMobile() {
        const { pageItems } = this.state;
        const allPagesItems = pageItems.flat();
        return (React.createElement(Fragment, null,
            React.createElement("div", { className: "c-carousel__items", onTouchEnd: this.onTouchEnd, onTouchMove: this.onTouchMove, onTouchStart: this.onTouchStart, ref: this.carouselItemsContainerRef }, allPagesItems.map((item, index) => {
                return this.renderCarouselItem(item, index);
            }))));
    }
    renderCarouselItem(item, index) {
        const { children } = this.props;
        const { page } = this.state;
        const isCurrentItem = page === index;
        const customPercentage = isCurrentItem ? undefined : 0;
        return (React.createElement("div", { className: "c-carousel__item", key: index, onClick: () => this.onClick(item), onDoubleClick: () => this.onDoubleClick(item) },
            propagateProps(children, item),
            this.isMobile() ? this.renderProgressBar(customPercentage) : null));
    }
    renderContainer() {
        const { children, uuid } = this.props;
        if (!this.context?.isDesigning()) {
            return null;
        }
        return (React.createElement(Container, { acceptedTypes: CarouselContainer.acceptedTypes, className: "u-grid-col-12", repeated: true, uuid: uuid }, children));
    }
    renderNav(event, icon, placement) {
        const { infinite } = this.props;
        const { page, pageItems } = this.state;
        const shouldRenderLeft = infinite || page > 0;
        const shouldRenderRight = infinite || page < pageItems.length - 1;
        const shouldRenderNav = placement === Placement.LEFT
            ? shouldRenderLeft
            : shouldRenderRight;
        if (this.isMobile() || !shouldRenderNav) {
            return null;
        }
        const className = classNames({
            [`c-carousel__nav-container--${placement}`]: isEnumValid(Placement, placement),
            'c-carousel__nav-container': true,
        });
        return (React.createElement("div", { className: className },
            React.createElement(CarouselButton, { icon: icon, onClick: event })));
    }
    renderTitle() {
        const { title } = this.props;
        if (!title) {
            return null;
        }
        return (React.createElement("div", { className: "c-carousel__title" },
            React.createElement("span", { className: "c-carousel__title-text" }, MessageService.translate(title))));
    }
    renderCarousel() {
        const { page, pageItems } = this.state;
        return (React.createElement("div", { className: "c-carousel" },
            this.renderTitle(),
            this.renderNav(this.onNavLeft, ARROW_LEFT, Placement.LEFT),
            this.renderContent(),
            this.renderNav(this.onNavRight, ARROW_RIGHT, Placement.RIGHT),
            React.createElement(BulletTrail, { length: pageItems.length, page: page })));
    }
    render() {
        const { data } = this.props;
        if ((!Array.isArray(data) || isEmptyArrayOrObject(data)) && !this.context?.isDesigning()) {
            return React.createElement(NotFound, null);
        }
        if (!this.isMobile()) {
            return this.renderCarousel();
        }
        return (React.createElement(SuppressHeadingMargin, { uuid: "carousel-suppress-contextual-bar-margin-uuid" }, this.renderCarousel()));
    }
};
Carousel.contextType = EditorContext;
Carousel.propTypes = {
    autoplaySpeed: number,
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    data: arrayOf(object).notEnumerable(),
    infinite: bool,
    itemsPerPage: number,
    onClick: eventListener,
    onContextMenuClick: eventListener,
    onDoubleClick: eventListener,
    title: expression,
};
Carousel.defaultProps = {
    autoplaySpeed: 30,
    infinite: true,
    itemsPerPage: 1,
};
Carousel = __decorate([
    AwesomeComponent({ api: CarouselAPI, containers: [CarouselContainer], type: ComponentType.DATA_DISPLAY })
], Carousel);
export { Carousel, };
//# sourceMappingURL=carousel.js.map